// src/features/calendar/components/RealTimeNotifications.tsx
import React, { useEffect, useState } from 'react';
import { useCalendarStore } from '../store/calendarStore';

interface Notification {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
}

const RealTimeNotifications: React.FC = () => {
  const { recentUpdates, conflictingEvents } = useCalendarStore();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    if (recentUpdates.length > 0) {
      const latestUpdate = recentUpdates[0];
      const newNotification: Notification = {
        id: `${latestUpdate.id}-${Date.now()}`,
        message: getNotificationMessage(latestUpdate),
        type: getNotificationType(latestUpdate.type),
        timestamp: latestUpdate.timestamp,
      };

      setNotifications(prev => [newNotification, ...prev.slice(0, 4)]); // Keep only 5 notifications

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
      }, 5000);
    }
  }, [recentUpdates]);

  const getNotificationMessage = (update: { type: string; data?: unknown }) => {
    const eventData = update.data as { title?: string } | undefined;
    
    switch (update.type) {
      case 'event_created':
        return `New event created: ${eventData?.title || 'Untitled'}`;
      case 'event_updated':
        return `Event updated: ${eventData?.title || 'Untitled'}`;
      case 'event_deleted':
        return `Event deleted`;
      case 'calendar_shared':
        return `Calendar shared with you`;
      case 'permission_changed':
        return `Your permissions have been updated`;
      default:
        return 'Calendar updated';
    }
  };

  const getNotificationType = (type: string): 'info' | 'success' | 'warning' | 'error' => {
    switch (type) {
      case 'event_created':
        return 'success';
      case 'event_updated':
        return 'info';
      case 'event_deleted':
        return 'warning';
      case 'calendar_shared':
        return 'success';
      case 'permission_changed':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return '✅';
      case 'info':
        return 'ℹ️';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 border-green-500 text-green-800';
      case 'info':
        return 'bg-blue-100 border-blue-500 text-blue-800';
      case 'warning':
        return 'bg-yellow-100 border-yellow-500 text-yellow-800';
      case 'error':
        return 'bg-red-100 border-red-500 text-red-800';
      default:
        return 'bg-gray-100 border-gray-500 text-gray-800';
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`p-3 rounded-lg border-l-4 shadow-lg transition-all duration-300 ease-in-out ${getNotificationColor(notification.type)}`}
        >
          <div className="flex items-start space-x-2">
            <span className="text-lg">
              {getNotificationIcon(notification.type)}
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium">
                {notification.message}
              </p>
              <p className="text-xs opacity-75 mt-1">
                {new Date(notification.timestamp).toLocaleTimeString()}
              </p>
            </div>
            <button
              onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
              className="text-xs opacity-50 hover:opacity-100 transition-opacity"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
      
      {conflictingEvents.length > 0 && (
        <div className="p-3 rounded-lg border-l-4 bg-red-100 border-red-500 text-red-800 shadow-lg">
          <div className="flex items-start space-x-2">
            <span className="text-lg">⚠️</span>
            <div className="flex-1">
              <p className="text-sm font-medium">
                Event Conflicts Detected
              </p>
              <p className="text-xs opacity-75 mt-1">
                {conflictingEvents.length} event(s) have conflicts that need resolution
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealTimeNotifications;
